// useVersionChecker — notices a new deploy while a tab stays open and moves it onto
// the new bundle. Never reloads in place: a reload mid-page could cut off an
// autosave or a half-typed field. It waits for the next page change instead,
// which is a full document load of the page the user was going to anyway.

import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import appsignal from '~/appsignal'
import { useNotificationStore } from '~/stores/notificationStore'
import { PLACEHOLDER_VERSION, fetchDeployedVersion, reloadPage, runningVersion } from '~/utils/appVersion'

// Staff keep Cortex in the foreground for hours, so visibilitychange alone is not enough.
export const VERSION_CHECK_INTERVAL_MS = 5 * 60_000

export function useVersionChecker(): boolean {
  const { t } = useTranslation('common')
  const { pathname } = useLocation()
  const [outdated, setOutdated] = useState(false)
  const pathnameAtDetection = useRef<string | null>(null)
  const toastShown = useRef(false)

  useEffect(() => {
    const running = runningVersion()
    if (!running || running === PLACEHOLDER_VERSION || outdated) return

    let cancelled = false
    const check = async (): Promise<void> => {
      try {
        const deployed = await fetchDeployedVersion()
        if (!cancelled && deployed && deployed !== PLACEHOLDER_VERSION && deployed !== running) {
          setOutdated(true)
        }
      } catch (err) {
        // A network failure (offline laptop) just waits for the next check.
        if (err instanceof TypeError) return
        appsignal?.sendError(err instanceof Error ? err : new Error('Version check failed'), (span) => {
          span.setTags({ type: 'version-check-failure' })
          return span
        })
      }
    }

    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void check()
    }

    void check()
    const id = window.setInterval(() => void check(), VERSION_CHECK_INTERVAL_MS)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [outdated])

  useEffect(() => {
    if (!outdated || toastShown.current) return
    toastShown.current = true
    useNotificationStore.getState().showToast({
      type: 'info',
      title: t('appUpdate.title'),
      message: t('appUpdate.message'),
      durationMs: null,
    })
  }, [outdated, t])

  useEffect(() => {
    if (!outdated) return
    if (pathnameAtDetection.current === null) {
      pathnameAtDetection.current = pathname
      return
    }
    // Only a new page, not a search-param change: filters update the URL while typing.
    if (pathname !== pathnameAtDetection.current) reloadPage()
  }, [outdated, pathname])

  return outdated
}
